import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useIntl } from 'react-intl'

import { Button } from '@/shared/ui'
import { cn, focusRing } from '@/shared/ui/cn'
import { Filter, PanelLeftClose, X } from '@/shared/ui/icons'
import { useMediaQuery } from '@/shared/ui/use-media-query'
import type { FocusRef } from '@/shared/ui/use-list-focus'

import { usePopoverA11y } from './use-popover-a11y'

/** From `lg` the filters are a column beside the results; below it they open as a drawer. */
const wideQuery = '(min-width: 1200px)'

const collapsedKey = 'tms.filtersCollapsed'

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(collapsedKey) === 'true'
  } catch {
    return false
  }
}

/** Where a search screen's filters show: beside the results, collapsed, or as a drawer. */
export interface FilterSidebar {
  /** Wide enough for the column beside the results. */
  readonly isWide: boolean
  /** The column is hidden, remembered in this browser. */
  readonly isCollapsed: boolean
  readonly setCollapsed: (collapsed: boolean) => void
}

/** The filter sidebar's state, for the screen that lays out the rows above it. */
export function useFilterSidebar(): FilterSidebar {
  const isWide = useMediaQuery(wideQuery)
  const [isCollapsed, setState] = useState(readCollapsed)
  const setCollapsed = useCallback((collapsed: boolean) => {
    setState(collapsed)
    try {
      window.localStorage.setItem(collapsedKey, String(collapsed))
    } catch {
      // Storage unavailable: the choice lasts for this visit.
    }
  }, [])
  return { isWide, isCollapsed, setCollapsed }
}

/**
 * A node that stays mounted while it moves between the sidebar and the drawer as the window crosses
 * `lg`, so nothing rendered into it is lost, such as an open Save dialog's name or a group left open.
 * The returned ref places it in whichever of the two is shown.
 */
function useStableHost(className: string): readonly [HTMLElement, (slot: HTMLElement | null) => void] {
  const [host] = useState(() => {
    const element = document.createElement('div')
    element.className = className
    return element
  })
  const place = useCallback(
    (slot: HTMLElement | null) => {
      if (slot !== null && host.parentNode !== slot) {
        slot.append(host)
      }
    },
    [host],
  )
  return [host, place] as const
}

/**
 * Keeps the panel's top on screen in `--filter-panel-top`, so its height stops above the action bar
 * before the panel sticks under the header, not only once it has. The page growing above it, such
 * as a row of filter chips, moves it too.
 */
function usePanelTop(isShown: boolean): (panel: HTMLDivElement | null) => void {
  const [panel, setPanel] = useState<HTMLDivElement | null>(null)
  useEffect(() => {
    if (panel === null || !isShown) {
      return undefined
    }
    const measure = () => panel.style.setProperty('--filter-panel-top', `${panel.getBoundingClientRect().top}px`)
    measure()
    window.addEventListener('scroll', measure, { passive: true })
    window.addEventListener('resize', measure)
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure)
    observer?.observe(document.body)
    return () => {
      window.removeEventListener('scroll', measure)
      window.removeEventListener('resize', measure)
      observer?.disconnect()
    }
  }, [panel, isShown])
  return setPanel
}

/** Lines a row above the results up with the table while the sidebar shows beside it. */
export function filterInset(sidebar: FilterSidebar): string | false {
  return sidebar.isWide && !sidebar.isCollapsed && 'lg:pl-[calc(var(--filter-col)+1.5rem)]'
}

/**
 * The results area of a search screen with filters, from the mockups' `find-search/FilterLayout.jsx`
 * and `FilterPanel.jsx`. From `lg` the filters are a sticky column left of the results: a heading
 * with a button that hides the column, Open / Save and Clear All, then a collapsible group per
 * field. Hidden, a Filters button above the table, reading "N applied" once any apply, brings it
 * back; each press moves focus to the button that takes the pressed one's place. Below `lg` the
 * same filters open over the page as a drawer from the left, with Show results. Filtering is live,
 * so neither form has anything to submit.
 */
export function FilterLayout(props: {
  readonly sidebar: FilterSidebar
  /** The groups, one `FilterSection` per field. */
  readonly filters: ReactNode
  /** Open / Save, under the heading. */
  readonly savedSearches?: ReactNode
  /** How many filters apply, for the button that shows the hidden column. */
  readonly activeCount: number
  readonly onClear: () => void
  readonly isDrawerOpen: boolean
  readonly onCloseDrawer: () => void
  /** Takes the shown Filters control, where focus goes once the last applied filter is removed. */
  readonly fallbackRef?: FocusRef
  /** The results. */
  readonly children: ReactNode
}) {
  const intl = useIntl()
  const { sidebar, fallbackRef } = props
  const { isWide, isCollapsed, setCollapsed } = sidebar
  const slotId = useId()
  const headingId = useId()
  const showRef = useRef<HTMLButtonElement | null>(null)
  const hideRef = useRef<HTMLButtonElement | null>(null)
  const isFocusPending = useRef(false)

  const toggle = useCallback(
    (collapsed: boolean) => {
      isFocusPending.current = true
      setCollapsed(collapsed)
    },
    [setCollapsed],
  )
  const hide = useCallback(() => toggle(true), [toggle])
  const show = useCallback(() => toggle(false), [toggle])
  useEffect(() => {
    if (isFocusPending.current) {
      isFocusPending.current = false
      ;(isCollapsed ? showRef : hideRef).current?.focus()
    }
  }, [isCollapsed])

  const setShow = useCallback(
    (element: HTMLButtonElement | null) => {
      showRef.current = element
      fallbackRef?.(element)
    },
    [fallbackRef],
  )
  const isShown = isWide && !isCollapsed
  const placePanel = usePanelTop(isShown)
  const [savedHost, placeSaved] = useStableHost('flex min-w-0 flex-1 flex-wrap items-center gap-2')
  const [filtersHost, placeFilters] = useStableHost('')
  const filtersTitle = intl.formatMessage({ id: 'search.filters' })
  const active = props.activeCount > 0

  return (
    <>
      {props.savedSearches !== undefined && createPortal(props.savedSearches, savedHost)}
      {createPortal(props.filters, filtersHost)}
      <div className="lg:flex lg:gap-6">
        {isWide && (
          // The column runs the results' full height, with a rule on its right; the panel in it sticks under the page header.
          // z-30 keeps the panel's popups above the tables; its height, from where it sits, keeps it above the action bar.
          <div
            hidden={!isShown}
            className="min-h-[calc(100dvh-var(--shell-header-height,0px))] w-[var(--filter-col)] shrink-0 border-r border-divider pr-4"
          >
            <div
              id={slotId}
              ref={placePanel}
              className="sticky top-[var(--shell-header-height,0px)] z-30 flex max-h-[calc(100dvh-max(var(--filter-panel-top,0px),var(--shell-header-height,0px))-var(--shell-action-bar-height,0px)-1rem)] flex-col pt-1"
            >
              <div className="flex shrink-0 items-center justify-between pb-4">
                {/* Hidden with the column while collapsed, so it takes the fallback only while shown. */}
                <h2 id={headingId} ref={isShown ? fallbackRef : undefined} className="font-heading text-xl font-medium text-text-primary">
                  {filtersTitle}
                </h2>
                <button
                  type="button"
                  ref={hideRef}
                  onClick={hide}
                  aria-label={intl.formatMessage({ id: 'search.filters.close' })}
                  aria-expanded="true"
                  aria-controls={slotId}
                  className={cn(
                    'flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-text-secondary transition hover:bg-background-contrast-sm hover:text-primary-main',
                    focusRing,
                  )}
                >
                  <PanelLeftClose size={18} />
                </button>
              </div>
              <SavedRow place={placeSaved} onClear={props.onClear} />
              {/* -ml-1.5 pl-1.5: room for the focus ring of a control at the left edge, which the scroll box would clip. */}
              <form aria-labelledby={headingId} onSubmit={preventSubmit} className="-ml-1.5 min-h-0 flex-1 overflow-y-auto overscroll-contain pl-1.5 pr-3">
                <div ref={placeFilters} />
              </form>
            </div>
          </div>
        )}
        <div className="min-w-0 flex-1">
          {isWide && isCollapsed && (
            <div className="mb-3">
              <Button
                ref={setShow}
                variant={active ? 'contained' : 'outlined'}
                size="small"
                onClick={show}
                aria-expanded="false"
                aria-controls={slotId}
                {...(active && { 'aria-label': intl.formatMessage({ id: 'search.filters.activeLabel' }, { count: props.activeCount }) })}
              >
                {active ? (
                  <>
                    <Filter size={16} />
                    {intl.formatMessage({ id: 'search.filters.active' }, { count: props.activeCount })}
                  </>
                ) : (
                  filtersTitle
                )}
              </Button>
            </div>
          )}
          {props.children}
        </div>
      </div>
      {!isWide && (
        <FilterDrawer
          isOpen={props.isDrawerOpen}
          title={filtersTitle}
          placeSaved={placeSaved}
          placeFilters={placeFilters}
          onClear={props.onClear}
          onClose={props.onCloseDrawer}
        />
      )}
    </>
  )
}

function preventSubmit(event: FormEvent) {
  event.preventDefault()
}

const clearClass = cn('inline-flex min-h-[33px] shrink-0 cursor-pointer items-center whitespace-nowrap rounded text-sm text-primary-main hover:underline', focusRing)

/** Open / Save, wrapping so a notice drops below them, with Clear All on the right. */
function SavedRow({ place, onClear }: { readonly place: (slot: HTMLElement | null) => void; readonly onClear: () => void }) {
  const intl = useIntl()
  return (
    <div className="flex shrink-0 items-start justify-between gap-3 pb-3">
      <div ref={place} className="flex min-w-0 flex-1" />
      <button type="button" onClick={onClear} className={clearClass}>
        {intl.formatMessage({ id: 'search.filters.clearAll' })}
      </button>
    </div>
  )
}

/**
 * Below `lg`: the filters as a modal drawer from the left; Escape, the backdrop or Show results
 * closes it. Closed, it stays mounted but hidden, so Open / Save in it still opens a default search.
 */
function FilterDrawer(props: {
  readonly isOpen: boolean
  readonly title: string
  readonly placeSaved: (slot: HTMLElement | null) => void
  readonly placeFilters: (slot: HTMLElement | null) => void
  readonly onClear: () => void
  readonly onClose: () => void
}) {
  const intl = useIntl()
  const { isOpen, title, placeSaved, placeFilters, onClear, onClose } = props
  const panelRef = useRef<HTMLDialogElement>(null)
  const titleId = useId()
  usePopoverA11y(isOpen, panelRef, onClose)
  const submit = useCallback(
    (event: FormEvent) => {
      event.preventDefault()
      onClose()
    },
    [onClose],
  )

  return (
    <div hidden={!isOpen} className="fixed inset-0 z-50">
      {/* Pointer-only click-away; the keyboard closes it with Escape or Show results. */}
      <div aria-hidden="true" onClick={onClose} className="absolute inset-0 cursor-default bg-[rgba(20,20,20,0.64)]" />
      {/* Open in place rather than with showModal, whose top layer would leave a select's menu, rendered in a portal, inert. */}
      <dialog
        open
        ref={panelRef}
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="absolute inset-y-0 left-0 right-auto m-0 flex h-full max-h-none w-80 max-w-[85vw] flex-col border-0 border-r border-line bg-background p-0 text-text-primary focus:outline-hidden"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-border-strong px-4 py-3">
          <h2 id={titleId} className="font-heading text-xl font-medium text-text-primary">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={intl.formatMessage({ id: 'search.filters.close' })}
            className={cn('cursor-pointer rounded text-text-secondary hover:text-text-primary', focusRing)}
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-4 pt-3">
          <SavedRow place={placeSaved} onClear={onClear} />
        </div>
        <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pl-4 pr-5">
            <div ref={placeFilters} />
          </div>
          <div className="shrink-0 border-t border-border-strong px-3 py-3">
            <Button type="submit" className="w-full" size="small">
              {intl.formatMessage({ id: 'search.filters.showResults' })}
            </Button>
          </div>
        </form>
      </dialog>
    </div>
  )
}
